import React, { useState, useRef, useCallback } from "react";
import emailjs from "@emailjs/browser";

const ContactForm = () => {
  const formRef = useRef();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));

      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    },
    [errors]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!validateForm()) return;

      setIsSubmitting(true);
      setSubmitStatus(null);

      try {
        const result = await emailjs.sendForm(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
          formRef.current,
          process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        );

        if (result.text === "OK") {
          setSubmitStatus("success");
          setFormData({ name: "", email: "", company: "", message: "" });
        } else {
          throw new Error("Failed to send email");
        }
      } catch (error) {
        console.error("Error sending email:", error);
        setSubmitStatus("error");
      } finally {
        setIsSubmitting(false);
      }
    },
    [validateForm]
  );

  return (
    <section className="contact-section" id="contact" aria-label="Get in touch">
      <div className="container">
        <div className="contact-grid">
          <div className="contact-info">
            <p className="section-label">Get Started</p>
            <h2>Let's build something that actually launches.</h2>
            <p>
              Tell us about your project and we'll figure out the best way to
              work together.
            </p>

            <div className="contact-details">
              <div className="contact-detail-item">
                <div className="contact-detail-icon" aria-hidden="true">
                  &#9993;
                </div>
                <div>
                  <h4>Email</h4>
                  <p>contact@haydenbulktechlabs.com</p>
                </div>
              </div>
              <div className="contact-detail-item">
                <div className="contact-detail-icon" aria-hidden="true">
                  &#9201;
                </div>
                <div>
                  <h4>Response Time</h4>
                  <p>Within 24 hours</p>
                </div>
              </div>
              <div className="contact-detail-item">
                <div className="contact-detail-icon" aria-hidden="true">
                  &#9872;
                </div>
                <div>
                  <h4>Location</h4>
                  <p>Toronto, Canada</p>
                </div>
              </div>
            </div>
          </div>

          <form
            ref={formRef}
            className="contact-form"
            onSubmit={handleSubmit}
            noValidate
          >
            {submitStatus === "success" && (
              <div className="form-alert form-alert-success">
                <h4>Message Sent!</h4>
                <p>
                  Thank you for reaching out. We'll get back to you within 24
                  hours.
                </p>
              </div>
            )}

            {submitStatus === "error" && (
              <div className="form-alert form-alert-error">
                <h4>Something went wrong</h4>
                <p>There was an issue sending your message. Please try again.</p>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="name">
                Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`form-input${errors.name ? " error" : ""}`}
              />
              {errors.name && <p className="form-error">{errors.name}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="email">
                Email <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`form-input${errors.email ? " error" : ""}`}
              />
              {errors.email && <p className="form-error">{errors.email}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="company">Company</label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">
                Message <span className="required">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows="5"
                value={formData.message}
                onChange={handleChange}
                className={`form-textarea${errors.message ? " error" : ""}`}
              />
              {errors.message && (
                <p className="form-error">{errors.message}</p>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary form-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
